import { Component, Input, ViewChild, Output, EventEmitter, effect, inject } from '@angular/core';
import { StepperSelectionEvent, STEPPER_GLOBAL_OPTIONS, CdkStep } from '@angular/cdk/stepper';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { select, Store } from '@ngxs/store';
import { MatStepper } from '@angular/material/stepper';
import { VoiceSubscriptionOffering, VoiceAllowedChangeInfo } from '../../models/offerings-response.model';
import {
  VoiceProductUpdateAction,
  ProductSubscriberUpdateAction,
  SetSelectedStepIndexAction,
  ProductPhoneNumberUpdateAction,
  VoiceProductUpdateSponsorAction,
  VoiceProductUpdateSponsorTypeAction,
} from '@sales/states/customer-product/customer-product.actions';
import { ControlContainer, FormGroupDirective } from '@angular/forms';
import { CustomerProductSelectors } from '@sales/states/customer-product/customer-product.selectors';
import { BindingType } from '@shared/models/subscription-offering.model';
import { OfferingSelectors } from '@sales/states/offering/offering.selectors';
import { Sponsor, SponsorType } from '@shared/models/sponsor.model';
import { AdditionalServicesStepComponent } from './additional-services-step/additional-services-step.component';
import { UserDefinerStepComponent } from './user-definer-step/user-definer-step.component';
import { NumberChooserStepComponent } from './number-chooser-step/number-chooser-step.component';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { OrderBuilderStepComponent } from './order-builder-step/order-builder-step.component';
import { IceStepperComponent } from '@app/shared/components/molecules/ice-stepper/ice-stepper.component';

@Component({
  selector: 'app-subscription-stepper',
  templateUrl: './subscription-stepper.component.html',
  styleUrls: ['./subscription-stepper.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
  imports: [
    IceStepperComponent,
    CdkStep,
    OrderBuilderStepComponent,
    IceButtonComponent,
    NumberChooserStepComponent,
    UserDefinerStepComponent,
    AdditionalServicesStepComponent,
  ],
})
export class SubscriptionStepperComponent {
  private store = inject(Store);
  formGroupDirective = inject(FormGroupDirective);

  @Input() product: VoiceProduct;
  @Input() canSellMobilePhone: boolean;
  @Input() isEquipmentUpgradeEnabled: boolean;
  @ViewChild('stepper') stepper: MatStepper;
  @Output() finishSubscription = new EventEmitter();

  selectedStep = select(CustomerProductSelectors.selectedStep);
  allOfferings = select(OfferingSelectors.getAllOfferings);

  offerings: VoiceAllowedChangeInfo[] = [];
  chooseSubscriptionHasError: boolean;
  numberChooserHasError: boolean;
  userHasError: boolean;
  additionalServicesHasError: boolean;
  sponsor: Sponsor;

  constructor() {
    effect(() => {
      if (!this.product) return;

      let offerings: VoiceSubscriptionOffering[];

      if (!this.canSellMobilePhone) {
        offerings = this.allOfferings().filter(
          offering => !(offering.bindings?.length > 0 && offering.bindings.find(b => b.bindingType === BindingType.Handset))
        );
      } else {
        offerings = this.allOfferings();
      }

      this.offerings = offerings.map(simOffering => ({ subscriptionOffering: simOffering }) as VoiceAllowedChangeInfo);
    });
  }

  setChooseSubscriptionHasError(invalid: boolean) {
    this.chooseSubscriptionHasError = invalid;
  }
  setNumberChooserHasError(invalid: boolean) {
    this.numberChooserHasError = invalid;
  }
  setUserHasError(invalid: boolean) {
    this.userHasError = invalid;
  }
  setAdditionalServicesHasError(invalid: boolean) {
    this.additionalServicesHasError = invalid;
  }
  updateSubscriberInState({ productId, subscriber }) {
    this.store.dispatch(new ProductSubscriberUpdateAction(productId, subscriber));
  }
  stepperOnChange(event: StepperSelectionEvent) {
    const interactedSteps = this.formGroupDirective.form.controls.interactedSteps.value;
    if (!interactedSteps.includes(event.previouslySelectedIndex)) {
      this.formGroupDirective.form.controls.interactedSteps.setValue([...interactedSteps, event.previouslySelectedIndex]);
    }
    this.store.dispatch(new SetSelectedStepIndexAction(event.selectedIndex));
  }
  onProductChange(event) {
    this.store.dispatch(new VoiceProductUpdateAction(event.productId, event.offering, event.changeInfo));
  }
  onUpdatePhoneNumber({ phoneNumberValues, productId }) {
    this.store.dispatch(new ProductPhoneNumberUpdateAction(productId, phoneNumberValues));
  }
  toNumberChooserStep() {
    this.store.dispatch(new SetSelectedStepIndexAction(1));
  }
  onSponsorSelected({ productId, seller }) {
    const sponsor: Sponsor = {
      sponsorId: seller.userId,
      fullName: seller.fullName,
      email: seller.email,
      team: null,
      sponsorType: SponsorType.Ice,
    };

    this.store.dispatch(new VoiceProductUpdateSponsorAction(productId, sponsor));
  }
  onSponsorTypeSelected({ productId, sponsorType }) {
    this.store.dispatch(new VoiceProductUpdateSponsorTypeAction(productId, sponsorType));
  }
  hasInteractedWithStep(stepIndex: number): boolean {
    return this.formGroupDirective.form.controls.interactedSteps?.value.includes(stepIndex);
  }
}
